import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  FormControlLabel,
  Autocomplete,
  RadioGroup,
  Radio,
  FormLabel,
  FormControl,
} from '@mui/material';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import CampaignTemplates from '../components/CampaignTemplates';
import { getSmsSegmentCount, SMS_NO_REPLY_SUFFIX } from '../utils/smsSegments';

interface CampaignActivity {
  _id: string;
  channel: 'email' | 'sms';
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'partially_failed';
  jobId: string;
  jobReferenceId?: string;
  subject?: string;
  message?: string;
  templateType?: 'auction' | 'offmarket' | 'general' | 'other';
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  failedRecipients?: string[];
  unitCostCents: number;
  totalCostCents: number;
  refundedCents?: number;
  context?: {
    propertyAddress?: string;
    auctionDateTime?: string;
    agentName?: string;
    agencyName?: string;
  };
  createdAt: string;
  completedAt?: string;
}

interface CampaignStats {
  totalCampaigns: number;
  emailCampaigns: number;
  smsCampaigns: number;
  totalRecipients: number;
  totalSent: number;
  totalFailed: number;
  totalCostCents: number;
  totalRefundedCents: number;
  completed: number;
  failed: number;
  partiallyFailed: number;
}

const MarketCampaign: React.FC = () => {
  const { agency, user } = useAuth();
  const isAdmin = user?.primaryRole === 'admin';
  const [activities, setActivities] = useState<CampaignActivity[]>([]);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);
  const [activityTab, setActivityTab] = useState<'all' | 'email' | 'sms'>('all');
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null);
  const [failedRecipients, setFailedRecipients] = useState<{ [key: string]: string[] }>({});
  const [loadingFailedRecipients, setLoadingFailedRecipients] = useState<{ [key: string]: boolean }>({});
  const [resendingActivityId, setResendingActivityId] = useState<string | null>(null);
  
  // Campaign sending state
  const [showSendCampaignDialog, setShowSendCampaignDialog] = useState(false);
  const [campaignType, setCampaignType] = useState<'email' | 'sms' | 'both'>('email');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [attendeesList, setAttendeesList] = useState<Array<{ _id: string; name: string; email?: string; phone?: string; propertyId?: string }>>([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [campaignPricing, setCampaignPricing] = useState<{ emailCostCents: number; smsCostCents: number; smsEnabled?: boolean } | null>(null);
  const [searchPropertyId, setSearchPropertyId] = useState<string>('all'); // Property for filtering search results
  const [campaignTargetPropertyId, setCampaignTargetPropertyId] = useState<string>(''); // Required property for campaign context
  const prevSearchPropertyIdRef = useRef<string>('all'); // Track previous search property to detect changes
  const [properties, setProperties] = useState<Array<{ _id: string; address?: any; description?: string }>>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<Array<{ id: string; name: string; subject: string; body?: string; html?: string }>>([]);
  const [smsTemplates, setSmsTemplates] = useState<Array<{ id: string; name: string; message: string }>>([]);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState<string>('');
  const [selectedSmsTemplateId, setSelectedSmsTemplateId] = useState<string>('');
  const [campaignTargetProperty, setCampaignTargetProperty] = useState<{ _id: string; address?: any; description?: string } | null>(null);
  const [budgetTargetInput, setBudgetTargetInput] = useState('');
  const [budgetRangePercentInput, setBudgetRangePercentInput] = useState('10');
  const [appliedBudgetTarget, setAppliedBudgetTarget] = useState<number | null>(null);
  const [appliedBudgetRangePercent, setAppliedBudgetRangePercent] = useState<number>(10);
  const lastFetchParamsRef = useRef<{ propertyId?: string; budgetTarget?: number | null; budgetRangePercent?: number } | null>(null);

  const smsDisabled = campaignPricing?.smsEnabled === false;



  const fetchCampaignActivity = async () => {
    if (!agency?.slug) return;
    try {
      setActivitiesLoading(true);
      setActivitiesError(null);
      const channel = activityTab === 'all' ? undefined : activityTab;
      const res = await api.get(`/agencies/${agency.slug}/campaign-activity`, {
        params: { channel, limit: 50 },
      });
      setActivities(res.data.activities || []);
    } catch (e: any) {
      setActivitiesError(e?.response?.data?.error || e?.message || 'Failed to fetch campaign activity');
    } finally {
      setActivitiesLoading(false);
    }
  };

  // If SMS is globally disabled, force campaign type back to email when necessary
  useEffect(() => {
    if (smsDisabled && (campaignType === 'sms' || campaignType === 'both')) {
      setCampaignType('email');
    }
  }, [smsDisabled, campaignType]);

  const fetchCampaignStats = async () => {
    if (!agency?.slug) return;
    try {
      const res = await api.get(`/agencies/${agency.slug}/campaign-activity/stats`, {
        params: { days: 30 },
      });
      setStats(res.data);
    } catch (e: any) {
      console.error('Failed to fetch campaign stats:', e);
    }
  };

  const fetchFailedRecipients = async (activityId: string) => {
    if (!agency?.slug) return;
    try {
      setLoadingFailedRecipients(prev => ({ ...prev, [activityId]: true }));
      const res = await api.get(`/agencies/${agency.slug}/campaign-activity/${activityId}/failed`);
      setFailedRecipients(prev => ({ ...prev, [activityId]: res.data.failedRecipients || [] }));
    } catch (e: any) {
      console.error('Failed to fetch failed recipients:', e);
      setFailedRecipients(prev => ({ ...prev, [activityId]: [] }));
    } finally {
      setLoadingFailedRecipients(prev => ({ ...prev, [activityId]: false }));
    }
  };

  const handleResendFailed = async (activity: CampaignActivity) => {
    if (!agency?.slug) return;
    if (!window.confirm(`Resend campaign to ${activity.failedCount} failed recipient(s)? This will use campaign credits.`)) {
      return;
    }

    try {
      setResendingActivityId(activity._id);
      await api.post(`/agencies/${agency.slug}/campaign-activity/${activity._id}/resend-failed`);
      alert(`Resend queued successfully. The campaign will be sent to ${activity.failedCount} recipient(s).`);
      // Refresh activity list after a delay
      setTimeout(() => {
        fetchCampaignActivity();
        fetchCampaignStats();
      }, 2000);
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error || e?.message || 'Failed to resend campaign';
      alert(errorMsg);
    } finally {
      setResendingActivityId(null);
    }
  };

  const fetchCampaignPricing = async () => {
    if (!agency?.slug) return;
    try {
      const res = await api.get(`/agencies/${agency.slug}/campaign-credits/pricing`);
      setCampaignPricing(res.data);
    } catch (e) {
      console.error('Failed to fetch campaign pricing:', e);
    }
  };

  const fetchProperties = async () => {
    if (!agency?.slug || !user?.id) return;
    try {
      setLoadingProperties(true);
      const { getProperties } = await import('../services/propertyService');
      const res = await getProperties(agency.slug, 1, 200, false);
      const propertiesList = Array.isArray(res) ? res : (res.properties || []);
      // Filter to only properties the user manages
      const managedProperties = propertiesList.filter((p: any) => {
        const id = user.id;
        return (
          p.status !== 'archived' &&
          (p.primaryAssignee === id ||
          p.secondaryAssignee === id ||
          p.tertiaryAssignee === id ||
          user?.primaryRole === 'admin')
        );
      });
      setProperties(managedProperties);
    } catch (e) {
      console.error('Failed to fetch properties:', e);
      setProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  const fetchCampaignTemplates = async () => {
    if (!agency?.slug) return;
    try {
      const res = await api.get(`/agencies/${agency.slug}/campaign-templates`);
      setEmailTemplates(res.data?.emailTemplates || []);
      setSmsTemplates(res.data?.smsTemplates || []);
    } catch (e) {
      console.error('Failed to fetch templates:', e);
      setEmailTemplates([]);
      setSmsTemplates([]);
    }
  };

  const parseBudgetInputToNumber = (value: string): number | null => {
    const cleaned = value.replace(/[\s,$]/g, '').trim();
    if (!cleaned) return null;
    const lower = cleaned.toLowerCase();
    const hasSuffix = lower.endsWith('m') || lower.endsWith('k');
    const multiplier = lower.endsWith('m') ? 1_000_000 : lower.endsWith('k') ? 1_000 : 1;
    const numericPart = lower.replace(/[mk]$/, '');
    const num = Number(numericPart);
    if (Number.isNaN(num) || num <= 0) return null;
    if (hasSuffix) return num * multiplier;
    if (num < 20) return num * 1_000_000;
    if (num < 1000) return num * 1_000;
    return num;
  };

  const fetchAttendees = async (propertyId?: string, budgetTarget?: number | null, budgetRangePercent?: number) => {
    if (!agency?.slug) return;
    try {
      setLoadingAttendees(true);
      // Use visitors endpoint to get attendees for campaigns
      const params: any = { limit: 1000 };
      if (propertyId && propertyId !== 'all') {
        params.propertyId = propertyId;
      }
      if (budgetTarget !== null && budgetTarget !== undefined) {
        params.budgetTarget = budgetTarget;
        params.budgetRangePercent = budgetRangePercent || 10;
      }
      const res = await api.get(`/agencies/${agency.slug}/visitors`, { params });
      const visitors = Array.isArray(res.data) ? res.data : (res.data?.visitors || []);
      console.log('[MarketCampaign] Fetched visitors:', visitors.length, visitors);
      
      // Extract unique attendees from visitors (visitors are AttendeeActivity records)
      const attendeeMap = new Map<string, { _id: string; name: string; email?: string; phone?: string; propertyId?: string }>();
      visitors.forEach((v: any) => {
        // The API returns attendeeId as an object, not attendee
        const attendeeData = v.attendeeId || v.attendee;
        if (attendeeData?._id) {
          const attendeeId = typeof attendeeData._id === 'string' ? attendeeData._id : attendeeData._id.toString();
          
          if (!attendeeMap.has(attendeeId)) {
            const name = attendeeData.name || 
              `${attendeeData.firstName || ''} ${attendeeData.lastName || ''}`.trim() || 
              `${v.firstName || ''} ${v.lastName || ''}`.trim() || 
              'Unknown';
            
            const email = attendeeData.email || v.email;
            const phone = attendeeData.phone || v.phone;
            const propertyId = v.propertyId?._id || v.propertyId;
            
            // Only add if has email or phone (at least one contact method)
            if (email || phone) {
              attendeeMap.set(attendeeId, {
                _id: attendeeId,
                name,
                email,
                phone,
                propertyId: propertyId ? (typeof propertyId === 'string' ? propertyId : propertyId.toString()) : undefined,
              });
            }
          }
        }
      });
      
      const attendees = Array.from(attendeeMap.values());
      console.log('[MarketCampaign] Extracted attendees:', attendees.length, attendees);
      setAttendeesList(attendees);
    } catch (e) {
      console.error('Failed to fetch attendees:', e);
      setAttendeesList([]);
    } finally {
      setLoadingAttendees(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchCampaignStats();
    }
    fetchCampaignActivity();
    fetchCampaignPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency?.slug, activityTab, isAdmin]);

  useEffect(() => {
    if (showSendCampaignDialog) {
      if (properties.length === 0 && !loadingProperties) {
        fetchProperties();
      }
      if (emailTemplates.length === 0 && smsTemplates.length === 0) {
        fetchCampaignTemplates();
      }
      // If searching a specific property (not 'all'), automatically set it as target property
      if (searchPropertyId !== 'all' && searchPropertyId && !campaignTargetPropertyId) {
        // Find property from current properties list
        const property = properties.find(p => p._id === searchPropertyId) || null;
        if (property) {
          setCampaignTargetPropertyId(searchPropertyId);
          setCampaignTargetProperty(property);
        }
      }
      // Only clear target property if we're switching FROM a specific property TO 'all'
      // Don't clear if already on 'all' or if user has manually selected a target property
      const prevSearchPropertyId = prevSearchPropertyIdRef.current;
      if (searchPropertyId === 'all' && prevSearchPropertyId !== 'all' && prevSearchPropertyId) {
        // Switching from specific property to 'all' - clear target property
        setCampaignTargetPropertyId('');
        setCampaignTargetProperty(null);
      }
      // Update ref for next render
      prevSearchPropertyIdRef.current = searchPropertyId;
      
      // Only fetch attendees if we have a valid property or 'all' is selected
      // Prevent duplicate fetches with the same parameters
      const fetchPropertyId = searchPropertyId !== 'all' ? searchPropertyId : undefined;
      const currentParams = { propertyId: fetchPropertyId, budgetTarget: appliedBudgetTarget, budgetRangePercent: appliedBudgetRangePercent };
      const lastParams = lastFetchParamsRef.current;
      const paramsChanged = !lastParams || 
        lastParams.propertyId !== currentParams.propertyId ||
        lastParams.budgetTarget !== currentParams.budgetTarget ||
        lastParams.budgetRangePercent !== currentParams.budgetRangePercent;
      
      if (paramsChanged && (searchPropertyId === 'all' || (searchPropertyId && properties.find(p => p._id === searchPropertyId)))) {
        lastFetchParamsRef.current = currentParams;
        fetchAttendees(
          fetchPropertyId,
          appliedBudgetTarget,
          appliedBudgetRangePercent
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSendCampaignDialog, searchPropertyId, appliedBudgetTarget, appliedBudgetRangePercent]);

  // Sync campaignTargetProperty with campaignTargetPropertyId when properties are loaded
  useEffect(() => {
    if (campaignTargetPropertyId && properties.length > 0) {
      const property = properties.find(p => p._id === campaignTargetPropertyId);
      if (property) {
        // Always update to ensure we have the correct reference from the options array
        setCampaignTargetProperty(property);
      } else {
        // Property ID is set but property not found in list - clear it
        setCampaignTargetProperty(null);
      }
    } else if (!campaignTargetPropertyId) {
      // Property ID is cleared - clear the property object
      setCampaignTargetProperty(null);
    }
  }, [campaignTargetPropertyId, properties]);

  const handleSearchPropertyChange = (propertyId: string) => {
    const previousSearchPropertyId = searchPropertyId;
    setSearchPropertyId(propertyId);
    // Update ref immediately so useEffect can use it
    prevSearchPropertyIdRef.current = previousSearchPropertyId;
    
    // If searching a specific property (not 'all'), automatically set it as target property
    if (propertyId !== 'all') {
      setCampaignTargetPropertyId(propertyId);
      const property = properties.find(p => p._id === propertyId) || null;
      setCampaignTargetProperty(property);
      // Re-apply templates if they're already selected
      if (selectedEmailTemplateId && emailTemplates.length > 0) {
        const template = emailTemplates.find(t => t.id === selectedEmailTemplateId);
        if (template) {
          handleEmailTemplateSelect(selectedEmailTemplateId);
        }
      }
      if (selectedSmsTemplateId && smsTemplates.length > 0) {
        const template = smsTemplates.find(t => t.id === selectedSmsTemplateId);
        if (template) {
          handleSmsTemplateSelect(selectedSmsTemplateId);
        }
      }
    } else if (previousSearchPropertyId !== 'all') {
      // Only clear target property if we're switching FROM a specific property TO 'all'
      // Don't clear if user is already on 'all' and has selected a target property
      setCampaignTargetPropertyId('');
      setCampaignTargetProperty(null);
    }
    fetchAttendees(
      propertyId !== 'all' ? propertyId : undefined,
      appliedBudgetTarget,
      appliedBudgetRangePercent
    );
  };


  const handleApplyBudgetFilter = () => {
    const parsedTarget = parseBudgetInputToNumber(budgetTargetInput);
    if (!parsedTarget) {
      alert('Please enter a valid target budget (e.g. 2.5M, 500k, or 1000000).');
      return;
    }
    const parsedRange = budgetRangePercentInput.trim().length === 0 ? 10 : Number(budgetRangePercentInput);
    if (Number.isNaN(parsedRange) || parsedRange < 0 || parsedRange > 100) {
      alert('Range % must be between 0 and 100.');
      return;
    }
    setAppliedBudgetTarget(parsedTarget);
    setAppliedBudgetRangePercent(parsedRange);
    fetchAttendees(
      searchPropertyId !== 'all' ? searchPropertyId : undefined,
      parsedTarget,
      parsedRange
    );
  };

  const handleClearBudgetFilter = () => {
    setAppliedBudgetTarget(null);
    setAppliedBudgetRangePercent(10);
    setBudgetTargetInput('');
    setBudgetRangePercentInput('10');
    fetchAttendees(
      searchPropertyId !== 'all' ? searchPropertyId : undefined,
      null,
      10
    );
  };

  const replaceTemplateVariables = (template: string, context: Record<string, string>): string => {
    let result = template;
    Object.keys(context).forEach((key) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, context[key] || '');
    });
    return result;
  };

  const getPropertyAddress = (property: any): string => {
    if (!property) return '';
    if (typeof property.address === 'string') return property.address;
    if (property.address?.street) {
      const parts = [
        property.address.street,
        property.address.suburb,
        property.address.state,
        property.address.postcode
      ].filter(Boolean);
      return parts.join(', ');
    }
    return property.description || property.address || '';
  };

  const handleEmailTemplateSelect = async (templateId: string) => {
    setSelectedEmailTemplateId(templateId);
    const template = emailTemplates.find(t => t.id === templateId);
    if (!template) return;

    // Build context from campaign target property and user/agency info
    const context: Record<string, string> = {
      firstName: '{{firstName}}', // Keep placeholder for bulk sending
      lastName: '{{lastName}}',
      name: '{{name}}',
      propertyAddress: campaignTargetProperty ? getPropertyAddress(campaignTargetProperty) : '',
      auctionDateTime: '',
      auctionLocation: '',
      priceGuide: '',
      keyFeatures: '',
      agentName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : (user?.email || ''),
      agencyName: agency?.name || '',
    };

    setEmailSubject(replaceTemplateVariables(template.subject || '', context));
    
    // Use body if available, otherwise use html (strip HTML tags for plain text)
    const templateContent = template.body || template.html || '';
    // Simple HTML tag removal for plain text display
    const plainTextContent = templateContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
    setEmailBody(replaceTemplateVariables(plainTextContent, context));
    
    if (template.html) {
      setEmailHtml(replaceTemplateVariables(template.html, context));
    }
  };

  const handleSmsTemplateSelect = async (templateId: string) => {
    setSelectedSmsTemplateId(templateId);
    const template = smsTemplates.find(t => t.id === templateId);
    if (!template) return;

    // Build context from campaign target property and user/agency info
    const context: Record<string, string> = {
      firstName: '{{firstName}}', // Keep placeholder for bulk sending
      lastName: '{{lastName}}',
      name: '{{name}}',
      propertyAddress: campaignTargetProperty ? getPropertyAddress(campaignTargetProperty) : '',
      auctionDateTime: '',
      auctionLocation: '',
      priceGuide: '',
      keyFeatures: '',
      agentName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : (user?.email || ''),
      agencyName: agency?.name || '',
    };

    setSmsMessage(replaceTemplateVariables(template.message || '', context));
  };

  const handleSendCampaign = async () => {
    if (!agency?.slug || attendeesList.length === 0) {
      alert('No attendees match your search criteria. Please adjust your filters.');
      return;
    }

    if (smsDisabled && (campaignType === 'sms' || campaignType === 'both')) {
      alert('SMS sending is temporarily disabled in the system.');
      return;
    }
    
    if (campaignType === 'email' || campaignType === 'both') {
      if (!emailSubject.trim() || (!emailBody.trim() && !emailHtml.trim())) {
        alert('Please provide email subject and body/html');
        return;
      }
    }
    
    if (campaignType === 'sms' || campaignType === 'both') {
      if (!smsMessage.trim()) {
        alert('Please provide SMS message');
        return;
      }
    }

    // Calculate cost
    if (!campaignPricing) {
      alert('Unable to fetch campaign pricing. Please try again.');
      return;
    }

    const emailAttendees = attendeesList.filter(a => a.email);
    const smsAttendees = attendeesList.filter(a => a.phone);
    const smsSegmentsPerMessage = getSmsSegmentCount(smsMessage.trim() + SMS_NO_REPLY_SUFFIX);
    const totalSmsSegmentsApprox = smsSegmentsPerMessage * smsAttendees.length;
    let totalCostCents = 0;
    if (campaignType === 'email' || campaignType === 'both') {
      totalCostCents += emailAttendees.length * campaignPricing.emailCostCents;
    }
    if (campaignType === 'sms' || campaignType === 'both') {
      totalCostCents += totalSmsSegmentsApprox * campaignPricing.smsCostCents;
    }

    const confirmMessage = `Send ${campaignType === 'both' ? 'email and SMS' : campaignType === 'email' ? 'email' : 'SMS'} campaign to ${attendeesList.length} recipient(s) matching your search criteria?\n\n` +
      (campaignType === 'email' || campaignType === 'both' 
        ? `Email: ${emailAttendees.length} × $${(campaignPricing.emailCostCents / 100).toFixed(2)} = $${((emailAttendees.length * campaignPricing.emailCostCents) / 100).toFixed(2)}\n`
        : '') +
      (campaignType === 'sms' || campaignType === 'both'
        ? `SMS: ~${totalSmsSegmentsApprox} segments × $${(campaignPricing.smsCostCents / 100).toFixed(2)} = $${((totalSmsSegmentsApprox * campaignPricing.smsCostCents) / 100).toFixed(2)}\n`
        : '') +
      `\nTotal: $${(totalCostCents / 100).toFixed(2)}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSendingCampaign(true);
    try {
      const promises = [];
      
    // Validate campaign target property is selected
    if (!campaignTargetPropertyId) {
      alert('Please select a target property for the campaign. This property will be used for campaign context.');
      return;
    }

    // Build context from campaign target property and user/agency info
    const context: any = {
      agentName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : (user?.email || ''),
      agencyName: agency?.name || '',
    };
    
    if (campaignTargetProperty) {
      context.propertyAddress = getPropertyAddress(campaignTargetProperty);
      // Could add more property context here if needed (auctionDateTime, priceGuide, etc.)
    }
      
      if (campaignType === 'email' || campaignType === 'both') {
        const emailAttendeeIds = emailAttendees.map(a => a._id);
        if (emailAttendeeIds.length > 0) {
          promises.push(
            api.post(`/agencies/${agency.slug}/visitors/bulk-email`, {
              attendeeIds: emailAttendeeIds,
              subject: emailSubject.trim(),
              body: emailBody.trim() || undefined,
              html: emailHtml.trim() || undefined,
              context,
            })
          );
        }
      }
      
      if (campaignType === 'sms' || campaignType === 'both') {
        const smsAttendeeIds = smsAttendees.map(a => a._id);
        if (smsAttendeeIds.length > 0) {
          promises.push(
            api.post(`/agencies/${agency.slug}/visitors/bulk-sms`, {
              attendeeIds: smsAttendeeIds,
              message: smsMessage.trim(),
              context,
            })
          );
        }
      }

      await Promise.all(promises);
      
      alert('Campaign(s) queued successfully!');
      setShowSendCampaignDialog(false);
      setEmailSubject('');
      setEmailBody('');
      setEmailHtml('');
      setSmsMessage('');
      setSearchPropertyId('all');
      setCampaignTargetPropertyId('');
      setCampaignTargetProperty(null);
      setSelectedEmailTemplateId('');
      setSelectedSmsTemplateId('');
      setAppliedBudgetTarget(null);
      setAppliedBudgetRangePercent(10);
      setBudgetTargetInput('');
      setBudgetRangePercentInput('10');
      fetchCampaignActivity();
    } catch (e: any) {
      const errorMsg = e?.response?.data?.error || e?.message || 'Failed to send campaign';
      alert(errorMsg);
    } finally {
      setSendingCampaign(false);
    }
  };

  if (!agency?.slug) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Marketing Campaign</Typography>
          <Box display="flex" gap={1}>
            <Button variant="contained" color="primary" onClick={() => setShowSendCampaignDialog(true)}>
              Send Campaign
            </Button>
          </Box>
        </Box>

        {/* Current campaign rates — live from system settings */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Current campaign rates</Typography>
            {campaignPricing ? (
              <Box display="flex" flexWrap="wrap" gap={3} alignItems="baseline">
                <Typography variant="body1">
                  <strong>Email:</strong> ${(campaignPricing.emailCostCents / 100).toFixed(2)} per recipient
                </Typography>
                <Typography variant="body1">
                  <strong>SMS:</strong> ${(campaignPricing.smsCostCents / 100).toFixed(2)} per segment
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (Rates from system settings · 160 characters = 1 SMS segment)
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Loading rates…</Typography>
            )}
          </CardContent>
        </Card>

        {/* Campaign fees explanation */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>How campaign fees work</Typography>
          <Typography variant="body2" component="span">
            <strong>Email:</strong> Charged per recipient at the rate above. &nbsp;
            <strong>SMS:</strong> Charged per segment at the rate above. Standard SMS has a 160-character limit per segment; messages longer than 160 characters are sent in multiple segments and charged per segment. Contract link SMS is also charged per segment.
          </Typography>
        </Alert>

        {isAdmin && (
          <>
        {/* Campaign Templates section */}
        <CampaignTemplates />

            {/* Campaign Activity Stats */}
            {stats && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Campaign Statistics (Last 30 Days)</Typography>
              <Box display="flex" flexWrap="wrap" gap={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Campaigns</Typography>
                  <Typography variant="h6">{stats.totalCampaigns}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="h6">{stats.emailCampaigns}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">SMS</Typography>
                  <Typography variant="h6">{stats.smsCampaigns}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Sent</Typography>
                  <Typography variant="h6">{stats.totalSent.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Cost</Typography>
                  <Typography variant="h6">${(stats.totalCostCents / 100).toFixed(2)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Refunded</Typography>
                  <Typography variant="h6">${(stats.totalRefundedCents / 100).toFixed(2)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
            )}
          </>
        )}

        {/* Campaign Activity History */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Campaign Activity</Typography>
              <Button onClick={fetchCampaignActivity} disabled={activitiesLoading} size="small">
                {activitiesLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>

            <Tabs value={activityTab} onChange={(_, newValue) => setActivityTab(newValue)} sx={{ mb: 2 }}>
              <Tab label="All" value="all" />
              <Tab label="Email" value="email" />
              <Tab label="SMS" value="sms" />
            </Tabs>

            {activitiesError && (
              <Alert severity="error" sx={{ mb: 2 }}>{activitiesError}</Alert>
            )}

            {activitiesLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : activities.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                No campaign activity found
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Channel</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Subject/Message</TableCell>
                      <TableCell>Recipients</TableCell>
                      <TableCell>Sent</TableCell>
                      <TableCell>Failed</TableCell>
                      <TableCell>Cost</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.map((activity) => (
                      <React.Fragment key={activity._id}>
                        <TableRow>
                          <TableCell>
                            {new Date(activity.createdAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={activity.channel.toUpperCase()} 
                              size="small"
                              color={activity.channel === 'email' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={activity.status.replace('_', ' ')}
                              size="small"
                              color={
                                activity.status === 'completed' ? 'success' :
                                activity.status === 'failed' ? 'error' :
                                activity.status === 'partially_failed' ? 'warning' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {activity.jobReferenceId?.startsWith('contract-sms-')
                                ? 'Contract link'
                                : (activity.subject || activity.message || '-')}
                            </Typography>
                          </TableCell>
                          <TableCell>{activity.totalRecipients}</TableCell>
                          <TableCell>{activity.sentCount}</TableCell>
                          <TableCell>
                            {activity.failedCount > 0 ? (
                              <Box>
                                <Typography variant="body2" color="error">
                                  {activity.failedCount}
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    if (expandedActivityId === activity._id) {
                                      setExpandedActivityId(null);
                                    } else {
                                      setExpandedActivityId(activity._id);
                                      if (!failedRecipients[activity._id] && !loadingFailedRecipients[activity._id]) {
                                        fetchFailedRecipients(activity._id);
                                      }
                                    }
                                  }}
                                  sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                                >
                                  {expandedActivityId === activity._id ? 'Hide' : 'Show'} failed
                                </Button>
                              </Box>
                            ) : (
                              activity.failedCount
                            )}
                          </TableCell>
                          <TableCell>
                            ${(activity.totalCostCents / 100).toFixed(2)}
                            {activity.refundedCents && activity.refundedCents > 0 && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Refunded: ${(activity.refundedCents / 100).toFixed(2)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {activity.failedCount > 0 && isAdmin && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                disabled={resendingActivityId === activity._id}
                                onClick={() => handleResendFailed(activity)}
                              >
                                {resendingActivityId === activity._id ? 'Resending...' : 'Resend Failed'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        {expandedActivityId === activity._id && activity.failedCount > 0 && (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                              {loadingFailedRecipients[activity._id] ? (
                                <Box display="flex" justifyContent="center" p={2}>
                                  <CircularProgress size={20} />
                                </Box>
                              ) : failedRecipients[activity._id] ? (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Failed {activity.channel === 'email' ? 'Email Addresses' : 'Phone Numbers'}:
                                  </Typography>
                                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {failedRecipients[activity._id].map((recipient, idx) => (
                                      <Typography key={idx} variant="body2" sx={{ mb: 0.5, fontFamily: 'monospace' }}>
                                        {recipient}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No failed recipients found
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

      {/* Send Campaign Dialog */}
      <Dialog
        open={showSendCampaignDialog}
        onClose={() => !sendingCampaign && setShowSendCampaignDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Send Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Campaign Type</FormLabel>
              <RadioGroup
                row
                value={campaignType}
                onChange={(e) => setCampaignType(e.target.value as 'email' | 'sms' | 'both')}
              >
                <FormControlLabel value="email" control={<Radio />} label="Email" />
                <FormControlLabel value="sms" control={<Radio />} label="SMS" disabled={smsDisabled} />
                <FormControlLabel value="both" control={<Radio />} label="Email + SMS" disabled={smsDisabled} />
              </RadioGroup>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Search Property (Optional - filter attendees)</Typography>
            {loadingProperties ? (
              <CircularProgress size={20} />
            ) : (
              <Autocomplete
                options={[{ _id: 'all', description: 'All Properties' }, ...properties]}
                getOptionLabel={(option) => option._id === 'all' ? 'All Properties' : getPropertyAddress(option)}
                value={searchPropertyId === 'all' ? { _id: 'all', description: 'All Properties' } : properties.find(p => p._id === searchPropertyId) || null}
                onChange={(_, newValue) => {
                  handleSearchPropertyChange(newValue?._id || 'all');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select property to filter attendees or 'All Properties'"
                    variant="outlined"
                    helperText="Use this to filter which attendees to search. You can search across all properties."
                    sx={{ mb: 2 }}
                  />
                )}
              />
            )}

            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
              Campaign Target Property <span style={{ color: 'red' }}>*</span>
            </Typography>
            {loadingProperties ? (
              <CircularProgress size={20} />
            ) : (
              <Autocomplete
                options={properties}
                getOptionLabel={(option) => getPropertyAddress(option)}
                value={campaignTargetProperty}
                onChange={(_, newValue) => {
                  if (newValue) {
                    // Use the property object directly from Autocomplete to ensure reference match
                    setCampaignTargetPropertyId(newValue._id);
                    setCampaignTargetProperty(newValue);
                    // Update template content if templates are already selected
                    if (selectedEmailTemplateId && emailTemplates.length > 0) {
                      const template = emailTemplates.find(t => t.id === selectedEmailTemplateId);
                      if (template) {
                        handleEmailTemplateSelect(selectedEmailTemplateId);
                      }
                    }
                    if (selectedSmsTemplateId && smsTemplates.length > 0) {
                      const template = smsTemplates.find(t => t.id === selectedSmsTemplateId);
                      if (template) {
                        handleSmsTemplateSelect(selectedSmsTemplateId);
                      }
                    }
                  } else {
                    setCampaignTargetPropertyId('');
                    setCampaignTargetProperty(null);
                  }
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!value || !option) return false;
                  return option._id === value._id;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select property for campaign context (required)"
                    variant="outlined"
                    required
                    helperText="This property will be used for campaign template context (e.g. property address in emails/SMS)"
                    sx={{ mb: 2 }}
                  />
                )}
              />
            )}

            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Budget Search (Optional)</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-start' }}>
              <TextField
                label="Budget Target"
                value={budgetTargetInput}
                onChange={(e) => setBudgetTargetInput(e.target.value)}
                placeholder="e.g. 2.5M, 500k, 1000000"
                size="small"
                sx={{ flex: 1 }}
                helperText="Enter target budget (supports M, k, or plain number)"
              />
              <TextField
                label="Range %"
                value={budgetRangePercentInput}
                onChange={(e) => setBudgetRangePercentInput(e.target.value)}
                type="number"
                size="small"
                inputProps={{ min: 0, max: 100, step: 1 }}
                sx={{ width: 100 }}
                helperText="0-100%"
              />
              <Button
                variant="outlined"
                onClick={handleApplyBudgetFilter}
                disabled={loadingAttendees}
                sx={{ mt: 0.5 }}
              >
                Apply
              </Button>
              {appliedBudgetTarget !== null && (
                <Button
                  variant="text"
                  onClick={handleClearBudgetFilter}
                  disabled={loadingAttendees}
                  sx={{ mt: 0.5 }}
                >
                  Clear
                </Button>
              )}
            </Box>
            {appliedBudgetTarget !== null && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Budget filter: ${(appliedBudgetTarget / 1000000).toFixed(2)}M ± {appliedBudgetRangePercent}%
              </Alert>
            )}

            {loadingAttendees ? (
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Loading attendees...</Typography>
              </Box>
            ) : attendeesList.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No attendees found matching your search criteria. {appliedBudgetTarget !== null && 'Try adjusting your budget filter or property selection.'}
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{attendeesList.length} attendee{attendeesList.length !== 1 ? 's' : ''}</strong> match your search criteria.
                  {attendeesList.filter(a => a.email).length > 0 && (
                    <span> {attendeesList.filter(a => a.email).length} with email.</span>
                  )}
                  {attendeesList.filter(a => a.phone).length > 0 && (
                    <span> {attendeesList.filter(a => a.phone).length} with phone.</span>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Campaign will be sent to all matching attendees based on your campaign type selection.
                </Typography>
              </Alert>
            )}

            {(campaignType === 'email' || campaignType === 'both') && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Email Template</Typography>
                <Autocomplete
                  options={emailTemplates}
                  getOptionLabel={(option) => option.name}
                  value={emailTemplates.find(t => t.id === selectedEmailTemplateId) || null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      handleEmailTemplateSelect(newValue.id);
                    } else {
                      setSelectedEmailTemplateId('');
                      setEmailSubject('');
                      setEmailBody('');
                      setEmailHtml('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select email template (optional)"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <TextField
                  fullWidth
                  label="Email Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  sx={{ mb: 2 }}
                  required
                />
                <TextField
                  fullWidth
                  label="Email Body (Plain Text)"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  multiline
                  rows={4}
                  sx={{ mb: 2 }}
                  helperText="Either body or HTML is required"
                />
                <TextField
                  fullWidth
                  label="Email HTML"
                  value={emailHtml}
                  onChange={(e) => setEmailHtml(e.target.value)}
                  multiline
                  rows={6}
                  sx={{ mb: 2 }}
                  helperText="Either body or HTML is required"
                />
              </>
            )}

            {(campaignType === 'sms' || campaignType === 'both') && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>SMS Template</Typography>
                <Autocomplete
                  options={smsTemplates}
                  getOptionLabel={(option) => option.name}
                  value={smsTemplates.find(t => t.id === selectedSmsTemplateId) || null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      handleSmsTemplateSelect(newValue.id);
                    } else {
                      setSelectedSmsTemplateId('');
                      setSmsMessage('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select SMS template (optional)"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <TextField
                  fullWidth
                  label="SMS Message"
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  multiline
                  rows={4}
                  sx={{ mb: 2 }}
                  required
                  helperText={`${smsMessage.length} characters · ${getSmsSegmentCount(smsMessage + SMS_NO_REPLY_SUFFIX)} segment(s) per message (includes "no reply" suffix)`}
                />
              </>
            )}

            {campaignPricing && attendeesList.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Estimated Cost (live rates from system settings):</strong>
                  {(campaignType === 'email' || campaignType === 'both') && (
                    <span>
                      {' '}Email: {attendeesList.filter(a => a.email).length} × ${(campaignPricing.emailCostCents / 100).toFixed(2)} = ${((attendeesList.filter(a => a.email).length * campaignPricing.emailCostCents) / 100).toFixed(2)}
                    </span>
                  )}
                  {(campaignType === 'sms' || campaignType === 'both') && (
                    <span>
                      {' '}SMS: ~{getSmsSegmentCount(smsMessage + SMS_NO_REPLY_SUFFIX) * attendeesList.filter(a => a.phone).length} segments × ${(campaignPricing.smsCostCents / 100).toFixed(2)} = ${((getSmsSegmentCount(smsMessage + SMS_NO_REPLY_SUFFIX) * attendeesList.filter(a => a.phone).length * campaignPricing.smsCostCents) / 100).toFixed(2)}
                    </span>
                  )}
                  {' '}Total: ${(
                    ((campaignType === 'email' || campaignType === 'both' ? attendeesList.filter(a => a.email).length * campaignPricing.emailCostCents : 0) +
                    (campaignType === 'sms' || campaignType === 'both' ? getSmsSegmentCount(smsMessage + SMS_NO_REPLY_SUFFIX) * attendeesList.filter(a => a.phone).length * campaignPricing.smsCostCents : 0)) / 100
                  ).toFixed(2)}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSendCampaignDialog(false)} disabled={sendingCampaign}>
            Cancel
          </Button>
          <Button
            onClick={handleSendCampaign}
            variant="contained"
            disabled={
              sendingCampaign ||
              attendeesList.length === 0 ||
              !campaignTargetPropertyId ||
              (smsDisabled && (campaignType === 'sms' || campaignType === 'both'))
            }
          >
            {sendingCampaign ? 'Sending...' : `Send Campaign to ${attendeesList.length} Attendee${attendeesList.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
};

export default MarketCampaign;

